import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  Transform as TransformIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dataMappingApi, sisConfigurationApi, customIndicatorApi } from '../../../services/sisApi';
import { SISDataMapping } from '../../../types/sis';

const DataMapping: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<SISDataMapping | null>(null);
  const [selectedEntityType, setSelectedEntityType] = useState('school');
  const [formData, setFormData] = useState<Partial<SISDataMapping>>({
    entity_type: 'school',
    sis_field_path: '',
    emis_field: '',
    transformation_rule: '',
    is_active: true,
  });

  const queryClient = useQueryClient();

  // Fetch data mappings
  const { data: mappingsResponse, isLoading, error } = useQuery({
    queryKey: ['dataMappings', selectedEntityType],
    queryFn: () => dataMappingApi.getAll({ entity_type: selectedEntityType }),
  });

  // Fetch configurations for selection
  const { data: configsResponse } = useQuery({
    queryKey: ['sisConfigurations'],
    queryFn: () => sisConfigurationApi.getAll(),
  });

  // Fetch indicators for mapping
  const { data: indicatorsResponse } = useQuery({
    queryKey: ['customIndicators'],
    queryFn: () => customIndicatorApi.getAll({ is_active: true }),
  });

  // Create/Update mapping mutation
  const createMappingMutation = useMutation({
    mutationFn: (data: Partial<SISDataMapping>) => dataMappingApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataMappings'] });
      handleCloseDialog();
    },
  });

  const updateMappingMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<SISDataMapping> }) =>
      dataMappingApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataMappings'] });
      handleCloseDialog();
    },
  });

  // Delete mapping mutation
  const deleteMappingMutation = useMutation({
    mutationFn: (id: number) => dataMappingApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataMappings'] });
    },
  });

  // Test mapping mutation
  const testMappingMutation = useMutation({
    mutationFn: ({ id, sampleData }: { id: number; sampleData: any }) =>
      dataMappingApi.testMapping(id, sampleData),
  });

  const mappings = mappingsResponse?.data?.results || [];
  const configurations = configsResponse?.data?.results || [];
  const indicators = indicatorsResponse?.data?.results || [];

  const entityTypes = [
    { value: 'school', label: 'School' },
    { value: 'student', label: 'Student' },
    { value: 'teacher', label: 'Teacher' },
    { value: 'enrollment', label: 'Enrollment' },
    { value: 'indicator', label: 'Custom Indicator' },
  ];

  const handleOpenDialog = (mapping?: SISDataMapping) => {
    if (mapping) {
      setEditingMapping(mapping);
      setFormData(mapping);
    } else {
      setEditingMapping(null);
      setFormData({
        entity_type: selectedEntityType as any,
        sis_field_path: '',
        emis_field: '',
        transformation_rule: '',
        is_active: true,
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingMapping(null);
  };

  const handleSubmit = () => {
    if (editingMapping) {
      updateMappingMutation.mutate({
        id: editingMapping.id!,
        data: formData,
      });
    } else {
      createMappingMutation.mutate(formData);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this mapping?')) {
      deleteMappingMutation.mutate(id);
    }
  };

  const handleTestMapping = (mapping: SISDataMapping) => {
    const sampleData = {
      school_name: 'Test School',
      student_count: 450,
      teacher_count: 18,
    };
    
    testMappingMutation.mutate({
      id: mapping.id!,
      sampleData,
    });
  };

  // Sample field mappings for each entity type
  const getSampleMappings = (entityType: string) => {
    const samples: Record<string, Array<{ sis: string; emis: string; description: string }>> = {
      school: [
        { sis: 'school_name', emis: 'name', description: 'School name' },
        { sis: 'school_reg_number', emis: 'registration_number', description: 'Registration number' },
        { sis: 'district_id', emis: 'district.external_id', description: 'District reference' },
        { sis: 'establishment_year', emis: 'year_established', description: 'Year established' },
      ],
      student: [
        { sis: 'sis_reg_no', emis: 'student_id', description: 'Student ID' },
        { sis: 'fisrt_name', emis: 'first_name', description: 'First name (with typo fix)' },
        { sis: 'last_name', emis: 'last_name', description: 'Last name' },
        { sis: 'dob', emis: 'date_of_birth', description: 'Date of birth' },
      ],
      teacher: [
        { sis: 'emp_no', emis: 'employee_number', description: 'Employee number' },
        { sis: 'first_name', emis: 'first_name', description: 'First name' },
        { sis: 'position', emis: 'job_title', description: 'Job title/position' },
        { sis: 'sex', emis: 'gender', description: 'Gender' },
      ],
    };
    return samples[entityType] || [];
  };

  return (
    <>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Data Field Mapping</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Mapping
        </Button>
      </Box>

      {/* Entity Type Filter */}
      <Box sx={{ mb: 3 }}>
        <TextField
          select
          label="Entity Type"
          value={selectedEntityType}
          onChange={(e) => setSelectedEntityType(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {entityTypes.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Map fields between your SIS database and the EMIS system. Use JSONPath notation 
          for nested fields (e.g., "student.personal_info.name") and transformation rules 
          for data conversion.
        </Typography>
      </Alert>

      {/* Sample Mappings Accordion */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">
            Sample Field Mappings for {entityTypes.find(t => t.value === selectedEntityType)?.label}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SIS Field</TableCell>
                  <TableCell>EMIS Field</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSampleMappings(selectedEntityType).map((sample, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Chip label={sample.sis} variant="outlined" size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip label={sample.emis} variant="outlined" size="small" />
                    </TableCell>
                    <TableCell>{sample.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Loading */}
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load mappings: {(error as any)?.message || 'Unknown error'}
        </Alert>
      )}

      {/* Mappings Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Mappings - {entityTypes.find(t => t.value === selectedEntityType)?.label}
          </Typography>

          {mappings.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No mappings found for {selectedEntityType}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>SIS Field Path</TableCell>
                    <TableCell>EMIS Field</TableCell>
                    <TableCell>Transformation</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappings.map((mapping: any) => (
                    <TableRow key={mapping.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CodeIcon fontSize="small" color="primary" />
                          <Typography variant="body2" component="code">
                            {mapping.sis_field_path}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" component="code">
                          {mapping.emis_field}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {mapping.transformation_rule ? (
                          <Tooltip title={mapping.transformation_rule}>
                            <Chip
                              icon={<TransformIcon />}
                              label="Has Transform"
                              size="small"
                              color="secondary"
                            />
                          </Tooltip>
                        ) : (
                          <Chip label="Direct" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={mapping.is_active ? 'Active' : 'Inactive'}
                          color={mapping.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Test Mapping">
                          <IconButton
                            size="small"
                            onClick={() => handleTestMapping(mapping)}
                            disabled={testMappingMutation.isPending}
                          >
                            <TestIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(mapping)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(mapping.id!)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={open} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {editingMapping ? 'Edit Field Mapping' : 'Create Field Mapping'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Entity Type"
                  value={formData.entity_type || 'school'}
                  onChange={(e) => setFormData({ ...formData, entity_type: e.target.value as any })}
                >
                  {entityTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active || false}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                  label="Active Mapping"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SIS Field Path"
                  value={formData.sis_field_path || ''}
                  onChange={(e) => setFormData({ ...formData, sis_field_path: e.target.value })}
                  placeholder="e.g., school_name, student.personal_info.name"
                  helperText="Use dot notation for nested fields"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="EMIS Field"
                  value={formData.emis_field || ''}
                  onChange={(e) => setFormData({ ...formData, emis_field: e.target.value })}
                  placeholder="e.g., name, first_name"
                  helperText="Target field in EMIS system"
                />
              </Grid>
              {formData.entity_type === 'indicator' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="Custom Indicator"
                    value={formData.indicator || ''}
                    onChange={(e) => setFormData({ ...formData, indicator: Number(e.target.value) })}
                  >
                    {indicators.map((indicator: any) => (
                      <MenuItem key={indicator.id} value={indicator.id}>
                        {indicator.name} ({indicator.code})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Transformation Rule"
                  multiline
                  rows={3}
                  value={formData.transformation_rule || ''}
                  onChange={(e) => setFormData({ ...formData, transformation_rule: e.target.value })}
                  placeholder="Optional Python expression for data transformation"
                  helperText="e.g., value.upper(), int(value) if value else 0"
                />
              </Grid>
            </Grid>

            {/* Examples */}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Examples:</strong>
              </Typography>
              <Typography variant="caption" component="div">
                • SIS Field: "fisrt_name" → EMIS Field: "first_name" (typo correction)
              </Typography>
              <Typography variant="caption" component="div">
                • Transformation: "value.title() if value else ''" (title case with null check)
              </Typography>
              <Typography variant="caption" component="div">
                • SIS Field: "sex" → EMIS Field: "gender" + Transform: "'M' if value.lower() == 'male' else 'F'"
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.sis_field_path || 
              !formData.emis_field ||
              createMappingMutation.isPending ||
              updateMappingMutation.isPending
            }
          >
            {editingMapping ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DataMapping;